import { useState } from 'react'
import type { AllowedEmailRow, AllowedEmailSortBy } from '@ai-meeting/shared'
import { fmt } from '@/constants/ui-locale'
import { useUiStrings } from '@/hooks/useUiStrings'
import { useTenantDirectory } from '@/hooks/useTenantDirectory'
import TenantUserSelect from '@/components/TenantUserSelect'
import { ApiError } from '@/services/api'
import { createAllowedEmail, deleteAllowedEmail, fetchAllowedEmails } from '@/services/admin'
import AdminDataTable, { type AdminColumn } from './AdminDataTable'
import AdminSearchInput from './AdminSearchInput'
import { formatDateTime } from './adminFormat'
import { useAdminTable } from './useAdminTable'

interface AllowedEmailsTabProps {
  enabled: boolean
}

/** Ties the visible label to the picker, which renders its own control. */
const ADDRESS_FIELD_ID = 'allowed-email-address'

/**
 * Manages which addresses outside the company domains may sign in via SSO.
 *
 * The only admin tab that writes, so unlike its read-only siblings it owns an
 * add form, a revoke action and the reload that follows either.
 */
export default function AllowedEmailsTab({ enabled }: AllowedEmailsTabProps) {
  const { t, locale } = useUiStrings()
  const [email, setEmail] = useState('')
  const [note, setNote] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [formError, setFormError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)

  const table = useAdminTable<AllowedEmailRow, AllowedEmailSortBy>({
    fetcher: fetchAllowedEmails,
    defaultSortBy: 'createdAt',
    enabled,
  })

  const { users: directory } = useTenantDirectory(enabled)

  const handleAdd = async (event: React.FormEvent) => {
    event.preventDefault()
    if (submitting) return

    const trimmed = email.trim()
    if (!trimmed) {
      // The picker is a button, not a native control, so nothing stops an empty
      // submit for us any more. Saying so beats the click doing nothing at all.
      setNotice(null)
      setFormError(t.allowedEmailAddressRequired)
      return
    }

    setSubmitting(true)
    setFormError(null)
    setNotice(null)
    try {
      await createAllowedEmail({ email: trimmed, note: note.trim() || undefined })
      setEmail('')
      setNote('')
      setNotice(t.allowedEmailAdded)
      table.reload()
    } catch (error) {
      // The server's own message wins — a duplicate comes back as a 409 saying
      // so, which is more useful than a generic failure line.
      setFormError(error instanceof ApiError ? error.message : t.allowedEmailAddFailed)
    } finally {
      setSubmitting(false)
    }
  }

  const handleRemove = async (row: AllowedEmailRow) => {
    if (!window.confirm(fmt(t.allowedEmailRemoveConfirm, { email: row.email }))) return

    setFormError(null)
    setNotice(null)
    try {
      await deleteAllowedEmail(row.id)
      setNotice(t.allowedEmailRemoved)
      table.reload()
    } catch (error) {
      setFormError(error instanceof ApiError ? error.message : t.allowedEmailRemoveFailed)
    }
  }

  const columns: AdminColumn<AllowedEmailRow, AllowedEmailSortBy>[] = [
    { key: 'email', label: t.allowedEmailAddress, sortBy: 'email', render: (r) => r.email },
    {
      key: 'note',
      label: t.allowedEmailNote,
      sortBy: 'note',
      render: (r) => (
        <span className="block max-w-md truncate" title={r.note ?? ''}>
          {r.note ?? '—'}
        </span>
      ),
    },
    {
      key: 'createdBy',
      label: t.allowedEmailColAddedBy,
      sortBy: 'createdBy',
      render: (r) => r.createdBy ?? '—',
    },
    {
      key: 'createdAt',
      label: t.allowedEmailColAddedAt,
      sortBy: 'createdAt',
      render: (r) => formatDateTime(r.createdAt, locale),
    },
    {
      key: 'actions',
      label: '',
      align: 'right',
      render: (r) => (
        <button
          type="button"
          onClick={() => void handleRemove(r)}
          className="rounded-lg border border-[var(--border-color)] px-2.5 py-1 text-xs font-semibold text-[var(--error)] transition-colors hover:bg-[var(--error)]/10"
        >
          {t.allowedEmailRemove}
        </button>
      ),
    },
  ]

  return (
    <div className="flex flex-col gap-4">
      <p className="text-xs leading-relaxed text-[var(--text-tertiary)]">{t.allowedEmailsHelp}</p>

      <form onSubmit={handleAdd} className="flex flex-wrap items-end gap-3">
        <div className="flex min-w-[220px] flex-1 flex-col gap-1">
          <label
            htmlFor={ADDRESS_FIELD_ID}
            className="text-xs font-semibold text-[var(--text-secondary)]"
          >
            {t.allowedEmailAddress}
          </label>
          <TenantUserSelect
            id={ADDRESS_FIELD_ID}
            required
            value={email}
            onChange={setEmail}
            users={directory}
            disabled={submitting}
            placeholderLabel={t.allowedEmailPickPlaceholder}
            searchPlaceholder={t.allowedEmailSearchDirectory}
            tenantGroupLabel={t.allowedEmailTenantGroup}
            manualLabel={t.allowedEmailEnterManually}
            manualInputLabel={t.allowedEmailOtherAddress}
            backToListLabel={t.allowedEmailBackToList}
            noResultsLabel={t.allowedEmailNoDirectoryMatch}
          />
        </div>
        <label className="flex min-w-[220px] flex-1 flex-col gap-1">
          <span className="text-xs font-semibold text-[var(--text-secondary)]">
            {t.allowedEmailNote}
          </span>
          <input
            type="text"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder={t.allowedEmailNotePlaceholder}
            disabled={submitting}
            maxLength={255}
            className="input-field"
          />
        </label>
        <button
          type="submit"
          disabled={submitting}
          className="h-10 shrink-0 rounded-lg bg-[var(--accent)] px-4 text-xs font-semibold text-white transition-colors hover:bg-[var(--accent-hover)] disabled:opacity-50"
        >
          {submitting ? t.allowedEmailAdding : t.allowedEmailAdd}
        </button>
      </form>

      {directory.length > 0 && (
        <p className="text-xs text-[var(--text-tertiary)]">
          {fmt(t.allowedEmailDirectoryHint, { count: String(directory.length) })}
        </p>
      )}

      {formError && (
        <p role="alert" className="text-xs text-[var(--error)]">
          {formError}
        </p>
      )}
      {notice && (
        <p role="status" className="text-xs text-emerald-600 dark:text-emerald-400">
          {notice}
        </p>
      )}

      <AdminSearchInput
        value={table.search}
        placeholder={t.adminSearchAllowedEmails}
        onChange={table.setSearch}
      />

      <AdminDataTable
        columns={columns}
        rows={table.rows}
        rowKey={(r) => r.id}
        loading={table.loading}
        error={table.error}
        sortBy={table.sortBy}
        sortDir={table.sortDir}
        onToggleSort={table.toggleSort}
        page={table.page}
        totalPages={table.totalPages}
        total={table.total}
        onPageChange={table.setPage}
      />
    </div>
  )
}
